import { z } from "zod";
/**
 * O catálogo de estilos de arte dos marcadores de livro.
 *
 * Até aqui, a direção de arte de cada marcador era um texto livre que um modelo de texto
 * escrevia a cada conjunto: a variação vinha do acaso dele, e nenhum dos dois lados — a
 * redação de uma direção, ou o resultado dela — dava para versionar ou comparar. Aqui o
 * **tratamento visual** (fundo, luz, tipografia, sensação) é fixo e nomeado; só o **motivo**
 * do evento varia, em dois pedaços que o modelo de texto escreve: o **cenário** ("colinas e uma
 * cidade antiga") e o **ornamento** ("ramos de oliveira com azeitonas"). Assim as três faixas de
 * um conjunto dividem o motivo e se distinguem pelo tratamento, e um estilo pode ser escolhido
 * pelo nome.
 *
 * Por que dois pedaços: o primeiro ensaio pago (02/10/2026) pôs um motivo só, "nos cantos", e a
 * cidade antiga virou uma janelinha colada no canto do pergaminho. Um estilo de papel não tem
 * cenário (`lugarDoCenario: null`); um de céu tem, ao fundo. E não há uma cláusula de "paleta":
 * um bloco de cores soltas tingiu as pessoas de azul na faixa da noite — as cores moram dentro
 * do fundo e do título, onde valem para a arte e não para a foto. E nenhum texto do prompt usa a palavra
 * "acentos" para cor ("acentos nas cores da capa"): em português é também o nome dos sinais gráficos, e
 * duas faixas saíram com o título "Máteús 24" e "Mateüs 24". A luz também não nomeia a fonte ("luz de
 * vela"): o modelo a desenhou, uma vela no meio do pergaminho.
 *
 * Tudo é puro, como o resto do pacote. Mexer na redação de um estilo é mexer na imagem que
 * ele produz: **suba `VERSAO_DO_CATALOGO`** (o teste de redação-ouro cobra).
 */
export const VERSAO_DO_CATALOGO = 4;
export const ESTILOS_DE_ARTE = [
    {
        id: "amanhecer",
        nome: "Amanhecer dourado",
        descricao: "Céu claro de manhã cedo, calorosa e esperançosa.",
        fundo: "céu azul-claro com nuvens iluminadas de laranja",
        lugarDoCenario: "ao fundo",
        lugarDoOrnamento: "em primeiro plano",
        luz: "sol baixo vindo do fundo, quente (cerca de 3500 K), suave e difusa, com contraluz leve nas bordas das nuvens e dos cabelos",
        tipografia: "serifa azul-marinho com detalhe em dourado",
        sensacao: "calorosa e esperançosa",
    },
    {
        id: "pergaminho",
        nome: "Pergaminho antigo",
        descricao: "Papel envelhecido e ilustração a nanquim: sóbria, de manuscrito histórico.",
        fundo: "papel envelhecido cor de creme com manchas suaves e bordas queimadas",
        lugarDoCenario: null,
        lugarDoOrnamento: "em desenho a nanquim e aquarela verde-oliva, nos cantos",
        luz: "luz âmbar quente e macia vinda da frente à esquerda (a fonte de luz não aparece na imagem), com sombras longas e suaves",
        tipografia: "serifa clássica marrom-escura com detalhe em ouro velho",
        sensacao: "sóbria e acolhedora, de manuscrito histórico",
    },
    {
        id: "noite",
        nome: "Noite azul",
        descricao: "Céu noturno com estrelas e lua: serena e contemplativa.",
        fundo: "céu noturno azul-profundo com estrelas e uma lua grande e suave",
        lugarDoCenario: "ao fundo, com luzes de lamparinas acesas",
        lugarDoOrnamento: "em tons de prata, em primeiro plano",
        luz: "luz de lua vinda do alto à direita, suave, com um brilho quente de lamparina ao fundo",
        tipografia: "serifa branca com detalhe em dourado",
        sensacao: "serena e contemplativa",
    },
    {
        id: "aquarela",
        nome: "Aquarela suave",
        descricao: "Pinceladas leves sobre papel claro: delicada e acolhedora.",
        fundo: "papel de aquarela branco-quente com pinceladas translúcidas em azul-acinzentado e verde-água",
        lugarDoCenario: "em aquarela translúcida ao fundo",
        lugarDoOrnamento: "em aquarela pelos cantos",
        luz: "luz de janela difusa vinda da esquerda, neutra-quente, sem sombras duras",
        tipografia: "serifa leve cor de grafite",
        sensacao: "delicada e acolhedora",
    },
    {
        id: "entardecer",
        nome: "Entardecer âmbar",
        descricao: "Luz de fim de tarde com névoa baixa: nostálgica e acolhedora.",
        fundo: "céu âmbar e rosa-queimado com névoa baixa",
        lugarDoCenario: "em silhueta ao fundo",
        lugarDoOrnamento: "em silhueta em primeiro plano",
        luz: "sol poente lateral à direita, âmbar (cerca de 3000 K), com brilho dourado nos contornos das pessoas e névoa difusa",
        tipografia: "serifa creme com sombra suave",
        sensacao: "nostálgica e acolhedora",
    },
    {
        id: "linho",
        nome: "Linho minimalista",
        descricao: "Fundo liso e muito respiro, um só traço de cor: silenciosa e elegante.",
        fundo: "papel de linho off-white liso, com muito espaço vazio e um único toque de verde-sálvia",
        lugarDoCenario: null,
        lugarDoOrnamento: "em traço fino de nanquim, num só canto, com muito vazio ao redor",
        luz: "luz de estúdio frontal, suave e uniforme, sem sombra marcada",
        tipografia: "serifa grafite de traço fino",
        sensacao: "silenciosa e elegante",
    },
];
export const IDS_DE_ESTILO_DE_ARTE = ESTILOS_DE_ARTE.map((e) => e.id);
export const idDeEstiloSchema = z.enum(IDS_DE_ESTILO_DE_ARTE);
/**
 * Os três de reserva — o que o modelo de texto não consegue escolher, ou o interruptor desligado.
 * São os três do modelo "Manhã, pergaminho e noite" que o admin já usava: a luz muda de uma
 * faixa para a outra, e o resto do conjunto não.
 */
export const ESTILOS_DE_ARTE_PADRAO = [
    "amanhecer",
    "pergaminho",
    "noite",
];
/** Três estilos diferentes entre si: dois iguais seriam duas faixas repetidas. */
export const estiloIdsSchema = z
    .array(idDeEstiloSchema)
    .length(3)
    .refine((ids) => new Set(ids).size === ids.length, "Escolha três estilos diferentes");
const pedacoDoMotivo = z
    .string()
    .transform((t) => t.replace(/\s+/g, " ").trim())
    .pipe(z.string().min(3).max(100));
/**
 * O motivo do evento, em dois pedaços de ELEMENTOS DE CENA — sem pessoas, sem texto escrito, sem
 * marca: o `cenario` ("colinas e uma cidade antiga de pedra clara") e o `ornamento` ("ramos de
 * oliveira com azeitonas"). Um pode faltar (um tema sem paisagem, um sem objeto).
 */
export const motivoDoEventoSchema = z
    .object({ cenario: pedacoDoMotivo.optional(), ornamento: pedacoDoMotivo.optional() })
    .strict();
export function estiloDeArte(id) {
    return ESTILOS_DE_ARTE.find((e) => e.id === id);
}
/**
 * O texto de direção de arte de um estilo, o que o gabarito do marcador recebe como `estilo`.
 * Cabe nos `TAMANHO_MAXIMO_DO_ESTILO` caracteres de `estilosDoMarcadorSchema` com tudo ligado.
 */
export function renderizarEstilo(estilo, op = {}) {
    const cenario = op.motivo?.cenario && estilo.lugarDoCenario ? op.motivo.cenario : null;
    const ornamento = op.motivo?.ornamento ?? null;
    let t = `${estilo.nome.toLowerCase()}: ${estilo.fundo}`;
    if (cenario)
        t += `, com ${cenario} ${estilo.lugarDoCenario}`;
    if (ornamento)
        t += `; ${ornamento} ${estilo.lugarDoOrnamento}`;
    if (op.luzEspecifica) {
        t += `; luz do cenário: ${estilo.luz}; nas pessoas, só o contorno dessa luz: a cor da pele e da roupa não muda`;
    }
    t += `; título em ${estilo.tipografia}`;
    const cores = op.paletaDaCapa && op.paletaDaCapa.length > 0 ? op.paletaDaCapa.join(", ") : null;
    if (cores)
        t += `, com detalhes de cor tirados da capa do livro (${cores})`;
    t += `; sensação ${estilo.sensacao}`;
    return t;
}
//# sourceMappingURL=estilos-de-arte.js.map